import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { HIDDEN, requireSection } from '@framework/core/decorators';
import { useSection } from '../data/use-section';
import { useAuth } from '../auth/AuthProvider';
import { registerMounted } from '../location/index';
import type { Focus } from './DialogForm';

/**
 * The editor — JSONForms, the renderer set, the dialog — is behind a dynamic import.
 * An anonymous visitor never downloads a byte of it.
 */
const EditDialog = lazy(() => import('./EditDialog'));

/**
 * Where the built-in pencil sits inside the section's box.
 * `'none'` suppresses it entirely — use the `edit` handle to place your own.
 */
export type PencilAt =
  | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  | 'center' | 'none';

/** Handed to the render function so a view can trigger editing from anywhere. */
export interface EditHandle {
  /** True when this session may edit — false for visitors. */
  canEdit: boolean;
  /** Open the edit dialog. Safe to call when canEdit is false: it does nothing. */
  open: () => void;
}

export interface SectionUpdateProps<K extends string> {
  name: K;
  /**
   * `(content, edit) => …`
   *
   * The second argument is optional and lets you put the trigger wherever the design
   * wants it — inside a heading, on a hover overlay, in your own toolbar — instead of
   * accepting the default corner button.
   */
  children: (data: never, edit: EditHandle) => ReactNode;
  /** Where the built-in pencil goes. Default 'top-right'. 'none' to place your own. */
  pencil?: PencilAt;
  /** Optional live preview — called as the editor types, before saving. */
  preview?: (data: Record<string, unknown>) => void;
  /** Rendered when this session may not view the section. Defaults to nothing. */
  fallback?: ReactNode;
  /**
   * Narrow the editor to ONE row of one list field, for a page that is about that row.
   * The save is still a save of the whole section — see FocusedRow.
   */
  focus?: Focus;
}

export function SectionUpdate<K extends string>({
  name, children, preview, fallback, pencil = 'top-right', focus,
}: SectionUpdateProps<K>) {
  const { data, denied, version, canWrite, loading, error } = useSection(name);
  const { session } = useAuth();
  const [editing, setEditing] = useState(false);

  // So the app can always answer "what is displaying right now".
  useEffect(() => registerMounted(name), [name]);

  if (loading) return <div className="section-skeleton" data-section={name} />;

  // Not permitted (or not there) is a normal outcome, not an error: render the
  // fallback, or nothing at all. A visitor must never see a permissions message.
  if (denied) return <>{fallback ?? null}</>;

  if (error || !data) return <div className="section-error">Section “{name}” failed to load.</div>;

  const canEdit = Boolean(session) && canWrite;

  /* Hidden is a content decision, so it is stored with the content — see decorators/
     types.ts. A visitor gets the fallback, exactly as an unviewable section gives them:
     an absent band, not a gap where one used to be. Whoever may edit it still sees it,
     marked, because a switch you cannot find again is a switch nobody dares use. */
  // Read from the META and not only from the document: a section declared `always` has
  // no such field, and a value left in its stored JSON from before must not go on hiding
  // it — least of all the header, which is how anybody would reach the screen to undo it.
  const hideable = requireSection(name).fields.some((f) => f.key === HIDDEN);
  const hidden = hideable && Boolean((data as Record<string, unknown>)[HIDDEN]);
  if (hidden && !canEdit) return <>{fallback ?? null}</>;

  const edit: EditHandle = { canEdit, open: () => { if (canEdit) setEditing(true); } };

  return (
    <div className="section-host" data-section={name} data-hidden={hidden || undefined}>
      {children(data as never, edit)}

      {canEdit && pencil !== 'none' && (
        <div className="edit-mark" data-at={pencil}>
          {/* Beside the pencil rather than over the band: the answer to "why can nobody
              see this?" has to be where the person who hid it will next look. */}
          {hidden && <span className="edit-hidden">Hidden</span>}
          <button
            type="button"
            className="edit-pencil"
            title={`Edit ${name}`}
            aria-label={`Edit ${name}`}
            onClick={() => setEditing(true)}
          >
            ✎
          </button>
        </div>
      )}

      {editing && (
        <Suspense fallback={null}>
          <EditDialog
            name={name}
            version={version}
            data={data as unknown as Record<string, unknown>}
            preview={preview}
            focus={focus}
            onClose={() => setEditing(false)}
          />
        </Suspense>
      )}
    </div>
  );
}
